import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { User, Key, MapPin, Car } from 'lucide-react';
import './KPCardServices.css';

const ServicesSection = styled.section`
  padding: 4rem 0;
  background:hsl(0, 0.00%, 95.30%);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
  margin-top: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ServiceCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  border: 1px dashed #e0e0e0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &:hover {
    background: #FF0707;
    border-color: #FF0707;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 7, 7, 0.3);
  }

  @media (max-width: 768px) {
    aspect-ratio: auto;
    min-height: 200px;
    padding: 1.5rem;
  }
`;

const ServiceIcon = styled(motion.div)`
  width: 45px;
  height: 45px;
  background: transparent;
  border: 2px solid #FF0707;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FF0707;
  flex-shrink: 0;
  transition: all 0.3s ease;

  ${ServiceCard}:hover & {
    border-color: white;
    color: white;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #34495e;
  margin-bottom: 0.75rem;
  font-family: 'Inter', sans-serif;
  line-height: 1.2;
  transition: all 0.3s ease;

  ${ServiceCard}:hover & {
    color: #000000;
  }
`;

const ServiceDescription = styled.p`
  color: #7f8c8d;
  line-height: 1.4;
  font-size: 0.9rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.3s ease;

  ${ServiceCard}:hover & {
    color: white;
  }
`;

const ServiceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const KPCardServices = () => {
  const services = [
    {
      icon: <User size={20} />,
      title: "Parking\nMarshals",
      description: "KeyPlot Marshals are easy visible whilst manning on-street parking bays for customer convenience."
    },
    {
      icon: <Key size={20} />,
      title: "Security",
      description: "Kindly ensure you have locked your vehicle. KeyPlot is not responsible for any lost, stolen, or damaged items"
    },
    {
      icon: <MapPin size={20} />,
      title: "Technology",
      description: "KeyPlot's focus is to implement an innovative parking management solution for customer convenience."
    },
    {
      icon: <Car size={20} />,
      title: "Off\nStreet",
      description: "Take advantage of longer parking options at off-street locations."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 }
  };

  const iconVariants = {
    hidden: { opacity: 0, scale: 0, rotate: -180 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      rotate: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <ServicesSection>
      <Container>
        <ServicesGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              variants={cardVariants}
            >
              <ServiceHeader>
                <ServiceIcon
                  variants={iconVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {service.icon}
                </ServiceIcon>
                <ServiceTitle style={{ whiteSpace: 'pre-line' }}>
                  {service.title}
                </ServiceTitle>
              </ServiceHeader>
              <ServiceDescription>{service.description}</ServiceDescription>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};

export default KPCardServices; 